'use client';

/**
 * Import Wizard（M1 — 单 type 4 步骨架）
 * 步骤：1 下载模板 → 2 上传 + preview → 3 确认 → 4 结果
 * 详见 docs/modules/robot-manager/14-import-export-tool-prd.md §4 / §8
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { Download, Upload, AlertTriangle, CheckCircle2, FileWarning, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from '@/lib/toast';
import { downloadBlob } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';
import { ApiClientError } from '@/lib/api-client';

import {
  robotImportApi,
  type ImportType,
  type PreviewResponse,
  type ConfirmResponse,
} from '../../_lib/api/import';

type WizardStep = 'template' | 'upload' | 'review' | 'done';

interface Props {
  type: ImportType;
  /** 显示给用户的类型名（已经 i18n 后） */
  typeLabel: string;
}

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ACCEPT_MIME =
  '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

function errorCode(err: unknown): string | undefined {
  return err instanceof ApiClientError ? err.code : undefined;
}

async function runDownload(fn: () => Promise<Blob>, filename: string, failMsg: string) {
  try {
    downloadBlob(await fn(), filename);
    return true;
  } catch {
    toast.error(failMsg);
    return false;
  }
}

export function ImportWizard({ type, typeLabel }: Props) {
  const { t, locale } = useTranslation();
  const im = t.robotManager.import;

  const [step, setStep] = useState<WizardStep>('template');
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [done, setDone] = useState<ConfirmResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDownloadTemplate = useCallback(async () => {
    const ok = await runDownload(
      () => robotImportApi.downloadTemplate(type, locale),
      `${type}-template.xlsx`,
      im.toast.templateDownloadFailed,
    );
    if (ok) toast.success(im.toast.templateDownloaded);
  }, [type, locale, im]);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > MAX_FILE_BYTES) {
        toast.error(im.toast.fileTooLarge);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      setUploading(true);
      try {
        const res = await robotImportApi.preview(type, file);
        setPreview(res);
        setStep('review');
        if (res.summary.deduped) toast.info(im.toast.deduped);
      } catch (err) {
        const code = errorCode(err);
        toast.error(code ? `${im.toast.previewFailed}: ${code}` : im.toast.previewFailed);
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [type, im],
  );

  const handleConfirm = useCallback(async () => {
    if (!preview) return;
    setConfirming(true);
    try {
      const res = await robotImportApi.confirm(preview.batchId);
      setDone(res);
      setStep('done');
      toast.success(im.toast.importCompleted);
    } catch (err) {
      const code = errorCode(err);
      toast.error(code ? `${im.toast.confirmFailed}: ${code}` : im.toast.confirmFailed);
    } finally {
      setConfirming(false);
    }
  }, [preview, im]);

  const handleDownloadErrors = useCallback(async () => {
    if (!preview) return;
    await runDownload(
      () => robotImportApi.downloadErrorReport(preview.batchId),
      `${preview.batchId}-errors.xlsx`,
      im.toast.errorReportFailed,
    );
  }, [preview, im]);

  const handleReset = useCallback(() => {
    setStep('template');
    setPreview(null);
    setDone(null);
  }, []);

  return (
    <div className="space-y-6">
      <StepIndicator step={step} />

      {step === 'template' && (
        <section className="space-y-4">
          <h2 className="text-lg font-semibold">{im.template.title.replace('{type}', typeLabel)}</h2>
          <p className="text-sm text-muted-foreground">{im.template.desc}</p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleDownloadTemplate}>
              <Download className="mr-2 h-4 w-4" />
              {im.template.downloadBtn}
            </Button>
            <Button onClick={() => setStep('upload')}>{im.template.nextBtn}</Button>
          </div>
        </section>
      )}

      {step === 'upload' && (
        <section className="space-y-4">
          <h2 className="text-lg font-semibold">{im.upload.title}</h2>
          <p className="text-sm text-muted-foreground">{im.upload.desc}</p>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_MIME}
            onChange={handleFileChange}
            disabled={uploading}
            className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm
                       file:mr-4 file:rounded file:border-0 file:bg-secondary file:px-3 file:py-1.5
                       file:text-sm file:font-medium hover:file:bg-secondary/80"
          />
          {uploading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {im.upload.uploading}
            </div>
          )}
          <Button variant="ghost" onClick={() => setStep('template')} disabled={uploading}>
            {im.common.back}
          </Button>
        </section>
      )}

      {step === 'review' && preview && (
        <ReviewStep
          preview={preview}
          onConfirm={handleConfirm}
          onDownloadErrors={handleDownloadErrors}
          onReset={handleReset}
          confirming={confirming}
        />
      )}

      {step === 'done' && done && (
        <section className="space-y-4">
          <Alert>
            <CheckCircle2 className="h-4 w-4" />
            <AlertTitle>{im.done.title}</AlertTitle>
            <AlertDescription>
              {im.done.summary
                .replace('{rows}', String(done.successRows))
                .replace('{records}', String(done.sideEffects?.recordCount ?? done.successRows))}
              {done.sideEffects?.notes ? ` (${done.sideEffects.notes})` : ''}
            </AlertDescription>
          </Alert>
          <Button onClick={handleReset}>{im.done.restartBtn}</Button>
        </section>
      )}
    </div>
  );
}

// ============================================================================

function StepIndicator({ step }: { step: WizardStep }) {
  const { t } = useTranslation();
  const labels = t.robotManager.import.steps;
  const steps: { key: WizardStep; label: string }[] = [
    { key: 'template', label: labels.template },
    { key: 'upload', label: labels.upload },
    { key: 'review', label: labels.review },
    { key: 'done', label: labels.done },
  ];
  const activeIndex = steps.findIndex((s) => s.key === step);
  return (
    <ol className="flex items-center gap-2 text-sm">
      {steps.map((s, i) => {
        const state = i === activeIndex ? 'active' : i < activeIndex ? 'passed' : 'pending';
        return (
          <li key={s.key} className="flex items-center gap-2">
            <span
              className={`flex h-7 w-7 items-center justify-center rounded-full border text-xs font-medium ${STEP_DOT_CLASS[state]}`}
            >
              {i + 1}
            </span>
            <span className={state === 'active' ? 'font-medium' : 'text-muted-foreground'}>{s.label}</span>
            {i < steps.length - 1 && <span className="mx-2 text-muted-foreground">›</span>}
          </li>
        );
      })}
    </ol>
  );
}

const STEP_DOT_CLASS: Record<'active' | 'passed' | 'pending', string> = {
  active: 'border-primary bg-primary text-primary-foreground',
  passed: 'border-primary/60 bg-primary/10 text-primary',
  pending: 'border-border text-muted-foreground',
};

// ============================================================================

function ReviewStep({
  preview,
  onConfirm,
  onDownloadErrors,
  onReset,
  confirming,
}: {
  preview: PreviewResponse;
  onConfirm: () => void;
  onDownloadErrors: () => void;
  onReset: () => void;
  confirming: boolean;
}) {
  const { t } = useTranslation();
  const im = t.robotManager.import;
  const s = preview.summary;
  const hasErrors = s.errorRows > 0;
  // backend 已经裁好 top 50 ERROR rows — 这里不做二次过滤
  const previewErrors = useMemo(() => preview.errorPreview ?? [], [preview]);
  const moreCount = Math.max(0, s.errorRows - previewErrors.length);

  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold">{im.review.title}</h2>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label={im.review.totalRows} value={s.totalRows} />
        <StatCard label={im.review.successRows} value={s.successRows} tone="success" />
        <StatCard label={im.review.errorRows} value={s.errorRows} tone={s.errorRows > 0 ? 'danger' : 'muted'} />
        <StatCard label={im.review.warningRows} value={s.warningRows} tone={s.warningRows > 0 ? 'warning' : 'muted'} />
      </div>

      {hasErrors && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{im.review.errorAlertTitle}</AlertTitle>
          <AlertDescription>{im.review.errorAlertDesc}</AlertDescription>
        </Alert>
      )}

      {previewErrors.length > 0 && (
        <div className="rounded-md border">
          <div className="border-b bg-muted/40 px-3 py-2 text-sm font-medium">{im.review.errorListTitle}</div>
          <ul className="max-h-60 divide-y overflow-y-auto">
            {previewErrors.map((e) => (
              <li key={e.rowNo} className="px-3 py-2 text-xs">
                <span className="font-mono text-muted-foreground">#{e.rowNo}</span>{' '}
                {e.errorDetail.slice(0, 3).map((iss, idx) => (
                  <span key={idx} className="ml-2">
                    <Badge variant="outline" className="mr-1 font-mono">
                      {iss.field}
                    </Badge>
                    {iss.code}
                  </span>
                ))}
              </li>
            ))}
            {moreCount > 0 && (
              <li className="px-3 py-2 text-xs text-muted-foreground">
                {im.review.moreErrors.replace('{n}', String(moreCount))}
              </li>
            )}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <Button onClick={onConfirm} disabled={hasErrors || confirming}>
          {confirming && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {im.review.confirmBtn}
        </Button>
        {hasErrors && (
          <Button variant="outline" onClick={onDownloadErrors}>
            <FileWarning className="mr-2 h-4 w-4" />
            {im.review.downloadErrorReportBtn}
          </Button>
        )}
        <Button variant="ghost" onClick={onReset} disabled={confirming}>
          {im.review.cancelBtn}
        </Button>
      </div>
    </section>
  );
}

const STAT_TONE_CLASS: Record<'default' | 'success' | 'danger' | 'warning' | 'muted', string> = {
  default: '',
  success: 'text-emerald-600',
  danger: 'text-destructive',
  warning: 'text-amber-600',
  muted: 'text-muted-foreground',
};

function StatCard({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: number;
  tone?: 'default' | 'success' | 'danger' | 'warning' | 'muted';
}) {
  return (
    <div className="rounded-md border bg-card p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-2xl font-semibold ${STAT_TONE_CLASS[tone]}`}>{value}</div>
    </div>
  );
}
